import { useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Minus, Plus } from "lucide-react"

import { Badge, BadgeProps } from "@/components/ui/badge"

import type { MergedRow } from "./schemas"

const ROW_HEIGHT = 40

const columnHelper = createColumnHelper<MergedRow>()

function StatusBadge({ row }: { row: MergedRow }) {
  const { t } = useTranslation()

  if (row.parseStatus === "invalid") {
    return (
      <Badge variant="danger">{t("noCallList.importCSV.status.invalid")}</Badge>
    )
  }

  if (row.writeStatus === "success") {
    return (
      <Badge variant="success">
        {t("noCallList.importCSV.status.success")}
      </Badge>
    )
  }

  if (row.writeStatus === "error") {
    return (
      <Badge variant="danger">{t("noCallList.importCSV.status.error")}</Badge>
    )
  }

  const variantMap: Record<string, BadgeProps["variant"]> = {
    no_change: "default",
    not_found: "danger",
    ready: "success",
  }

  return (
    <Badge variant={variantMap[row.status] ?? "default"}>
      {t(`noCallList.importCSV.status.${row.status}`)}
    </Badge>
  )
}

function RowDetails({ row }: { row: MergedRow }) {
  const { t } = useTranslation()

  const translateKey = (key: string) => {
    return t(`noCallList.importCSV.warnings.${key}`, {
      defaultValue: t("noCallList.importCSV.warnings.INTERNAL_ERROR"),
    })
  }

  if (row.parseStatus === "invalid") {
    return (
      <span className="text-sm text-danger-300">
        {row.errors.map(translateKey).join("; ")}
      </span>
    )
  }

  if (row.writeStatus === "error" && row.writeErrors) {
    return (
      <span className="text-sm text-danger-300">
        {row.writeErrors.map(translateKey).join("; ")}
      </span>
    )
  }

  return null
}

function NoCallAction({ noCall }: { noCall: MergedRow["noCall"] }) {
  const { t } = useTranslation()

  if (noCall === undefined) {
    return ""
  }

  const isAdd = noCall === true
  const isRemove = noCall === false
  // invalid parsed rows can have any string value: False "1" "cocos" etc.
  const isInvalid = !isAdd && !isRemove

  return (
    <span className="flex items-center gap-1.5 text-sm font-medium">
      {!isInvalid && (
        <span
          className={`
            flex size-5 shrink-0 items-center justify-center rounded-full
            text-neutral-100
            ${isAdd ? `bg-danger-300` : `bg-success-300`}
          `}
        >
          {isAdd ? (
            <Plus className="size-3" strokeWidth={3} />
          ) : (
            <Minus className="size-3" strokeWidth={3} />
          )}
        </span>
      )}
      {isAdd
        ? t("noCallList.importCSV.noCallAction.add")
        : isRemove
          ? t("noCallList.importCSV.noCallAction.remove")
          : noCall}
    </span>
  )
}

function PreviewTable({ rows }: { rows: MergedRow[] }) {
  const parentRef = useRef<HTMLDivElement>(null)
  const columns = useColumns()

  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.rowIndex),
  })

  const tableRows = table.getRowModel().rows

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    estimateSize: () => ROW_HEIGHT,
    getScrollElement: () => parentRef.current,
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 10,
  })

  return (
    <div
      ref={parentRef}
      className="max-h-[400px] min-w-0 overflow-auto rounded-sm border"
    >
      <table className="table-fixed text-sm" style={{ minWidth: 800 }}>
        <colgroup>
          {table.getAllColumns().map((col) => (
            <col key={col.id} style={{ width: col.getSize() }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-neutral-100">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="truncate px-3 py-2 text-left font-medium"
                  style={{
                    maxWidth: header.getSize(),
                    minWidth: header.getSize(),
                    width: header.getSize(),
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          className="relative"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = tableRows[virtualRow.index]

            return (
              <tr
                key={row.id}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                className="absolute left-0 w-full border-b"
                style={{
                  minHeight: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const shouldWrap = ["details", "email", "phone"].includes(
                    cell.column.id,
                  )

                  return (
                    <td
                      key={cell.id}
                      className={
                        shouldWrap
                          ? "px-3 py-2 wrap-break-word"
                          : "truncate px-3 py-2"
                      }
                      style={{
                        maxWidth: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function useColumns() {
  const { t } = useTranslation()

  return [
    columnHelper.accessor("rowIndex", {
      header: t("noCallList.importCSV.columns.row"),
      size: 60,
    }),
    columnHelper.accessor((row) => row.phone ?? "", {
      id: "phone",
      header: t("noCallList.importCSV.columns.phone"),
      size: 150,
    }),
    columnHelper.accessor((row) => row.email ?? "", {
      id: "email",
      header: t("noCallList.importCSV.columns.email"),
      size: 300,
    }),
    columnHelper.display({
      id: "noCall",
      header: t("noCallList.importCSV.columns.noCall"),
      size: 100,
      cell: ({ row }) => <NoCallAction noCall={row.original.noCall} />,
    }),
    columnHelper.display({
      id: "status",
      header: t("noCallList.importCSV.columns.status"),
      size: 120,
      cell: ({ row }) => <StatusBadge row={row.original} />,
    }),
    columnHelper.display({
      id: "details",
      header: t("noCallList.importCSV.columns.details"),
      size: 250,
      cell: ({ row }) => <RowDetails row={row.original} />,
    }),
  ]
}

export { PreviewTable }
